import { useState } from "react";
import { Globe, User, Handshake, TrendingUp, MessageSquare } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { SITE, PARTNER_INTERESTS, INVESTOR_INTERESTS } from "@/content/site";
import { Reveal, StagePill } from "@/components/site/primitives";
import { EnquiryForm } from "@/components/site/EnquiryForm";

const OPTIONS = [
  { value: "general", icon: MessageSquare, title: "General enquiry", body: "Questions, feedback or media." },
  { value: "partnership", icon: Handshake, title: "Partnership enquiry", body: "Doctors, labs, pharmacies, hospitals, NGOs." },
  { value: "investment", icon: TrendingUp, title: "Investor & incubation", body: "Investors, incubators, mentors, competitions." },
];

export default function ContactPage() {
  const [type, setType] = useState("general");
  usePageMeta({
    title: "Contact SwastSaathi — General, Partnership & Investor Enquiries",
    description: "Contact SwastSaathi, founded by Aman Kumar, with general, healthcare partnership, or investment and incubation enquiries. Official website: swastsaathi.in.",
    path: "/contact",
  });
  const interests = type === "partnership" ? PARTNER_INTERESTS : type === "investment" ? INVESTOR_INTERESTS : [];

  return (
    <section data-testid="contact-page" className="relative overflow-hidden bg-mint">
      <div className="dot-grid absolute inset-0 opacity-50" aria-hidden="true" />
      <div className="container-x relative grid gap-14 py-16 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-5">
          <Reveal>
            <StagePill />
            <p className="eyebrow mt-8">Contact us</p>
            <h1 className="mt-4 text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">Let's start a conversation.</h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate md:text-lg">Choose the kind of enquiry below. Every message is read by the SwastSaathi team and answered by email.</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 space-y-3" as="div">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-plum/70" id="enquiry-type-label">Enquiry type</p>
            <div role="radiogroup" aria-labelledby="enquiry-type-label" className="space-y-3">
              {OPTIONS.map((o) => {
                const active = type === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setType(o.value)}
                    data-testid={`contact-type-${o.value}`}
                    className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-[background-color,border-color] duration-200 ${active ? "border-teal bg-white shadow-[0_10px_30px_-15px_rgba(11,122,114,0.5)]" : "border-plum/10 bg-white/60 hover:bg-white"}`}
                  >
                    <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${active ? "bg-teal text-white" : "bg-teal-50 text-teal-700"}`}><o.icon className="h-5 w-5" /></span>
                    <span>
                      <span className="block font-display font-semibold text-plum">{o.title}</span>
                      <span className="block text-sm text-slate">{o.body}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-white p-5" data-testid="contact-founder">
              <User className="h-5 w-5 text-teal-700" />
              <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate">Founder</p>
              <p className="font-display font-semibold text-plum">{SITE.founder}</p>
            </div>
            <div className="rounded-2xl bg-white p-5" data-testid="contact-website">
              <Globe className="h-5 w-5 text-teal-700" />
              <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate">Official website</p>
              <a href={SITE.url} className="font-display font-semibold text-plum hover:underline">{SITE.domain}</a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="card-soft p-6 sm:p-10 lg:col-span-7">
          <h2 className="text-2xl font-semibold">{OPTIONS.find((o) => o.value === type).title}</h2>
          <p className="mb-8 mt-2 text-sm text-slate">Fields marked <span className="text-magenta">*</span> are required.</p>
          <EnquiryForm key={type} mode="contact" defaultType={type} idPrefix="contact" interests={interests} interestLabel={type === "investment" ? "Area of interest" : "Partnership interest"} />
        </Reveal>
      </div>
    </section>
  );
}
